import React from 'react';
import Card from './Card';
import '../styles/Player.css';

const Player = ({ player, isCurrentPlayer, onCardPlay, position, gameStarted }) => {
  const { name, hand, isComputer } = player;
  
  const handleCardClick = (card) => {
    if (isCurrentPlayer && !isComputer && onCardPlay) {
      onCardPlay(card);
    }
  };
  
  // Max hand container widths per position (px) — keeps cards within bounds
  const MAX_HAND_WIDTH = {
    0: Math.min(window.innerWidth - 80, 1000), // human - nearly full screen width
    1: 220, // left side player
    2: 400, // top player
    3: 220, // right side player
  };

  // Card widths per position
  const CARD_WIDTH_MAP = {
    0: 90,  // human cards at scale 1.5 (base ~60px * 1.5)
    1: 80,
    2: 80,
    3: 80,
  };

  // Compute per-card margin so hand stays within MAX_HAND_WIDTH
  const getComputerCardMargin = (totalCards) => {
    const maxWidth = MAX_HAND_WIDTH[position];
    const cardWidth = CARD_WIDTH_MAP[position] ?? 80;
    const naturalWidth = totalCards * cardWidth;
    if (naturalWidth <= maxWidth) return 0;
    const margin = (maxWidth - naturalWidth) / (2 * totalCards);
    return Math.max(margin, -cardWidth * 0.65);
  };

  // Same logic for human player
  const getHumanCardMargin = (totalCards) => {
    const maxWidth = MAX_HAND_WIDTH[0];
    const cardWidth = CARD_WIDTH_MAP[0];
    const naturalWidth = totalCards * cardWidth;
    if (naturalWidth <= maxWidth) return 12; // default spacing
    const margin = (maxWidth - naturalWidth) / (2 * totalCards);
    return Math.max(margin, -cardWidth * 0.65);
  };

  // Arrange cards in a fan-like display or straight line
  const getCardStyle = (index, totalCards) => {
    // Create a fan effect for cards
    const maxRotation = 20; // Maximum rotation in degrees
    const midPoint = (totalCards - 1) / 2;
    const offset = index - midPoint;
    
    // For human player (bottom), create a straight line
    if (position === 0) {
      // Straight line layout with even spacing
      return {
        transform: 'none',
        zIndex: index
      };
    }
    // For computer player 2 (top), create an opposite arc pattern
    else if (position === 2) {
      // For top player, we want center cards to be closer to center of table
      // and outer cards to be further away
      const distanceFromCenter = Math.abs(offset);
      const translateY = distanceFromCenter * 5; // Push outer cards further away
      const rotation = (offset / midPoint) * maxRotation; // Keep same rotation direction
      
      return {
        transform: `rotate(${rotation}deg) translateY(${translateY}px)`,
        zIndex: totalCards - Math.abs(offset) // Center cards on top
      };
    } else {
      // Normal fan pattern for other players
      const rotation = (offset / midPoint) * maxRotation;
      const translateY = Math.abs(rotation) * -0.5; // Cards at edges are slightly higher
      
      return {
        transform: `rotate(${rotation}deg) translateY(${translateY}px)`,
        zIndex: index
      };
    }
  };
  
  return (
    <div className={`player player-position-${position} ${isCurrentPlayer ? 'current-player' : ''} ${isComputer ? 'computer-player' : 'human-player'}`}>
      {/* For computer player 2 (top), switch name and card count */}
      {position === 2 ? (
        <>
          <div className="card-count">
            Cards: {hand.length}
          </div>
          
          <div className="player-hand">
            {hand.map((card, index) => (
              <div 
                key={index} 
                className="card-wrapper"
                style={{ ...getCardStyle(index, hand.length), margin: `0 ${getComputerCardMargin(hand.length)}px` }}
              >
                <Card 
                  card={card} 
                  onClick={handleCardClick} 
                  playable={isCurrentPlayer && !isComputer}
                  isBack={isComputer}
                  position={position}
                />
              </div>
            ))}
          </div>
          
          <div className="player-name-row">
            <div className={`player-name${isCurrentPlayer ? ' active-turn' : ''}`}>{name}</div>
            {hand.length === 1 && <div className="uno-indicator">UNO!</div>}
          </div>
        </>
      ) : position === 1 || position === 3 ? (
        /* For computer player 1 (left) and 3 (right), flip the text */
        <>
          <div className="card-count flipped-text">
            Cards: {hand.length}
          </div>
          
          <div className="player-hand">
            {hand.map((card, index) => (
              <div 
                key={index} 
                className="card-wrapper"
                style={{ ...getCardStyle(index, hand.length), margin: `0 ${getComputerCardMargin(hand.length)}px` }}
              >
                <Card 
                  card={card} 
                  onClick={handleCardClick} 
                  playable={isCurrentPlayer && !isComputer}
                  isBack={isComputer}
                  position={position}
                />
              </div>
            ))}
          </div>
          
          <div className="player-name-row flipped-text">
            <div className={`player-name${isCurrentPlayer ? ' active-turn' : ''}`}>{name}</div>
            {hand.length === 1 && <div className="uno-indicator">UNO!</div>}
          </div>
        </>
      ) : (
        /* Normal layout for other players */
        <>
          <div className="player-name-row">
            <div className={`player-name${isCurrentPlayer && gameStarted ? ' active-turn' : ''}`} style={!gameStarted ? { color: 'white' } : {}}>{name}</div>
            {hand.length === 1 && <div className="uno-indicator">UNO!</div>}
          </div>
          
          <div className="player-hand">
            {hand.map((card, index) => (
              <div 
                key={index} 
                className="card-wrapper"
                style={{ ...getCardStyle(index, hand.length), margin: `0 ${getHumanCardMargin(hand.length)}px` }}
              >
                <Card 
                  card={card} 
                  onClick={handleCardClick} 
                  playable={isCurrentPlayer && !isComputer}
                  isBack={!gameStarted}
                  position={position}
                />
              </div>
            ))}
          </div>
          
          <div className="card-count">
            Cards: {hand.length}
          </div>
        </>
      )}
    </div>
  );
};

export default Player;
